import {
  CompanyMentionDebug,
  CompanyMentionDebugPanel,
  type CompanyMentionDebugInfo,
} from "@/components/ai-elements/company-mention-debug";
import CompanyTagHoverCard from "@/components/company/company-tag-hover-card";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { useCompanyPanelSurface } from "@/contexts/company-panel-surface-context";
import {
  useCurrentCompanyMention,
  type CurrentCompanyMention,
} from "@/contexts/current-company-mention-context";
import { useIsCompanyMentionDebugEnabled } from "@/hooks/useIsCompanyMentionDebugEnabled";
import { useTranslation } from "@/hooks/useTranslation";
import {
  companyTagOfMention,
  type CompanyMentionNodeProps,
} from "@/lib/company-mention-nodes";
import { companyIdentifierChecks } from "@/lib/company-mention-reliability";
import { hasTagHoverContent } from "@/lib/company-tag";
import { openCompanyTagPreview } from "@/lib/open-company-tag-preview";
import { cn } from "@/lib/utils";
import {
  companyPreviewPanelStore,
  isEntrySelected,
} from "@/stores/company-preview-panel-store";
import { companyPreviewLookupKey, type CompanyTag } from "@shaarp/common";
import { BuildingIcon, GlobeIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

// Hover dwell before the card opens, and grace before it closes — the latter
// lets the cursor travel from the chip onto the card without dismissing it.
const PREVIEW_OPEN_DELAY_MS = 350;
const PREVIEW_CLOSE_DELAY_MS = 150;

type CompanyMentionProps = CompanyMentionNodeProps & {
  children?: ReactNode;
};

/** Trims and lowercases a discriminator for case-insensitive equality checks. */
function normalizeDiscriminator(
  value: string | null | undefined,
): string | undefined {
  return value?.trim().toLowerCase() || undefined;
}

/**
 * Whether the tag designates the company whose page hosts the chat, matched on
 * any discriminator the tag carries.
 */
function isTagOfCompany(
  tag: CompanyTag,
  company: CurrentCompanyMention | null,
): boolean {
  if (!company) return false;
  const matches = (
    value: string | undefined,
    other: string | null,
  ): boolean =>
    !!value && normalizeDiscriminator(value) === normalizeDiscriminator(other);
  return (
    (!!tag.id && tag.id === company.id) ||
    matches(tag.siret, company.siret) ||
    matches(tag.siren, company.siren) ||
    matches(tag.domain, company.domain) ||
    (!!company.name && matches(tag.name, company.name))
  );
}

/**
 * The hover card's open state, debounced both ways so the popover survives the
 * cursor crossing the gap between the chip and the card.
 */
function useDelayedHoverCard() {
  const [isOpen, setIsOpen] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
  }, []);
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(
    () => () => {
      clearOpenTimer();
      cancelClose();
    },
    [clearOpenTimer, cancelClose],
  );

  const scheduleOpen = useCallback(() => {
    cancelClose();
    openTimerRef.current = setTimeout(
      () => setIsOpen(true),
      PREVIEW_OPEN_DELAY_MS,
    );
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    closeTimerRef.current = setTimeout(
      () => setIsOpen(false),
      PREVIEW_CLOSE_DELAY_MS,
    );
  }, [clearOpenTimer]);

  const closeNow = useCallback(() => {
    clearOpenTimer();
    cancelClose();
    setIsOpen(false);
  }, [clearOpenTimer, cancelClose]);

  return {
    isOpen,
    setIsOpen,
    scheduleOpen,
    scheduleClose,
    cancelClose,
    closeNow,
  };
}

/**
 * Whether the chip keyed `key` is the one shown in the side panel. Subscribed
 * through a selector returning a boolean, so a store update only re-renders the
 * chips whose selection flips, not every mention of the conversation.
 */
function useIsShownInPanel(key: string): boolean {
  return useSyncExternalStore(companyPreviewPanelStore.subscribe, () =>
    isEntrySelected(companyPreviewPanelStore.getSnapshot(), key),
  );
}

/**
 * Replays the chip's one-shot flash whenever it becomes the one in the panel
 * (including stepping back onto it with the chevrons). Driven by state so a
 * re-render on store changes does not wipe it.
 */
function useSelectionFlash(isSelected: boolean): boolean {
  const [isFlashing, setIsFlashing] = useState(false);
  useEffect(() => {
    setIsFlashing(false);
    if (!isSelected) return;
    const frame = requestAnimationFrame(() => setIsFlashing(true));
    return () => cancelAnimationFrame(frame);
  }, [isSelected]);
  return isFlashing;
}

/**
 * Inline, clickable company chip rendered by Streamdown for company tags. Every
 * tag with a name is a chip; hovering or clicking it makes no request — the hover
 * card and the side panel both show what the tag itself says. A tag carrying a
 * Shaarp id (a UUID) shows the building glyph, one without it the globe: the
 * company is known from the web only.
 */
export function CompanyMention(props: CompanyMentionProps) {
  const { children } = props;
  const { t } = useTranslation();
  const surface = useCompanyPanelSurface();
  const isDebugEnabled = useIsCompanyMentionDebugEnabled();
  const currentCompany = useCurrentCompanyMention();

  const tag = companyTagOfMention(props);
  const label =
    (typeof children === "string" && children.trim()) || tag?.name || "";
  // The key the web profile is filed under (`useCompanyWebPreview`): two chips
  // the server tells apart are two stops of the panel.
  const key = tag ? (companyPreviewLookupKey(tag) ?? tag.name) : "";

  const hoverCard = useDelayedHoverCard();
  const isSelected = useIsShownInPanel(key);
  const isFlashing = useSelectionFlash(isSelected);

  // The chip shown in the panel gets no hover card: it would duplicate the panel.
  const { closeNow: closeHoverCard } = hoverCard;
  useEffect(() => {
    if (isSelected) closeHoverCard();
  }, [isSelected, closeHoverCard]);

  // No name, no chip: a tag cannot be shown without one. Placed after every hook
  // so the early return never changes hook order.
  if (!tag) return <span>{label}</span>;

  // On a company's own page (the docked chat), a mention of that same company
  // is a redundant target — the user is already viewing it — so it renders as
  // plain text.
  const isCurrentCompany = isTagOfCompany(tag, currentCompany);

  // What the admin-only debug card shows on hover, whichever way the label renders.
  const debug: CompanyMentionDebugInfo = {
    marker: tag,
    status: isCurrentCompany ? "currentCompany" : "tag",
  };

  if (isCurrentCompany) {
    return (
      <CompanyMentionDebug debug={debug} isEnabled={isDebugEnabled}>
        <span className="font-medium text-foreground">{label}</span>
      </CompanyMentionDebug>
    );
  }

  // A Shaarp company only by a real id: the tag is model output, and a made-up
  // id would show the building glyph for a company the base does not hold.
  const isIdentified = companyIdentifierChecks({ id: tag.id }).id === "valid";
  const Icon = isIdentified ? BuildingIcon : GlobeIcon;
  const hasHoverCard = hasTagHoverContent(tag) && !isSelected;
  // Whether the hover card is on screen — then it carries the admin debug itself,
  // and the separate bubble stays closed rather than be covered by it.
  const isPreviewShown = hasHoverCard && hoverCard.isOpen;

  const trigger = (
    <button
      type="button"
      onClick={(event) =>
        openCompanyTagPreview({
          tag,
          key,
          trigger: event.currentTarget,
          surface,
        })
      }
      onMouseEnter={hasHoverCard ? hoverCard.scheduleOpen : undefined}
      onMouseLeave={hasHoverCard ? hoverCard.scheduleClose : undefined}
      // Every chip carries its key, label and tag so the side panel can build its
      // full ordered list of stops from the DOM, with no lookup.
      data-company-key={key}
      data-company-label={label}
      data-company-tag={JSON.stringify(tag)}
      data-identified={isIdentified}
      aria-label={t("company.mention.ariaLabel", { name: label })}
      className={cn(
        "inline-flex max-w-[220px] items-center gap-1 rounded-full px-2 py-0.5",
        "align-baseline text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2",
        "cursor-pointer bg-majorelle/10 text-majorelle hover:bg-majorelle/20 focus-visible:ring-majorelle/40",
        isSelected && "border border-[#5f3ffc]",
        isFlashing && "company-mention-flash",
      )}
    >
      <Icon className="inline-block size-3 shrink-0 align-[-0.125em]" />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );

  // Always the same wrapper, whether or not a hover card may open: swapping the
  // chip's parent structure would remount its DOM node and detach the click
  // target the panel reads its neighbours from, collapsing the navigable list to
  // a single entry. Eligibility only gates whether the card opens, never whether
  // the wrapper exists.
  return (
    <Popover open={isPreviewShown} onOpenChange={hoverCard.setIsOpen}>
      <CompanyMentionDebug
        debug={debug}
        isEnabled={isDebugEnabled}
        isSuppressed={isPreviewShown}
      >
        <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      </CompanyMentionDebug>
      {hasHoverCard && (
        <PopoverContent
          side="top"
          align="start"
          className="w-[320px] p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onMouseEnter={hoverCard.cancelClose}
          onMouseLeave={hoverCard.scheduleClose}
        >
          <CompanyTagHoverCard tag={tag} />
          {isDebugEnabled && (
            <div className="-mx-4 -mb-4 mt-4 rounded-b-md border-t border-warning bg-warning-foreground p-3">
              <CompanyMentionDebugPanel debug={debug} />
            </div>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
